// Core lib dependencies.
import React from 'react';
import PropTypes from 'prop-types';

import VimeoPlayer from '@vimeo/player';
import YoutubePlayer from 'react-youtube';
import Hls from 'hls.js';
import Plyr from 'plyr';

// Misc dependencies.
import { capitalize, sanitizeHTML, translateHref } from '../utils/string.js';
import * as assetUtils from '../utils/asset.js';
import { getRelativeDate } from '../utils/datetime.js';
import { requireSDK } from '../../legacy/utils/utils.js';
import _ from 'lodash';

// Model objects.
import DisplayItem from '../model/entity/DisplayItem/DisplayItem.js';
import PinnedDisplayItem from '../model/entity/PinnedDisplayItem.js';

// Config.
import enums from '../config/enums.js';

/**
 * <pre>
 * Video Player React components.
 *
 * Summary of responsibilities:
 *
 *  - set of components and helper functions to handle rendering and functionality for displaying videos
 * </pre>
 *
 * @author Various
 * @namespace Common.Components.VideoPlayers
 */

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
/* Components */

/**
 * <pre>
 * Vimeo.
 *
 * Summary of responsibilities:
 *
 * 	- extracts the id from the DisplayItem as appropriate to build the video url.
 *  - generates markup to output an embedded Vimeo video.
 * </pre>
 *
 * @namespace
 * @memberof Common.Components.VideoPlayers
 */
class Vimeo extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
    this.setIframeRef = this.setIframeRef.bind(this);
  }

  componentDidMount() {
    const player = this.getPlayer();
    if (this.props.isMuted) {
      player.setVolume(0);
    } else {
      player.setVolume(0.5);
    }
    if (this.props.shouldAutoPlay) player.play();
  }

  setIframeRef(domEle) {
    this.iframe = domEle;
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {ReactElement}
   */
  render() {
    const src = this.getSrc();
    const { width } = this.props.styles;
    return (
      <div className="xEmbed xEmbedVimeo xEmbedVideoPlayer" style={{ width: `${width}px` }}>
        <iframe src={src} ref={this.setIframeRef} />
      </div>
    );
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Utility methods */

  getPlayer() {
    const player = new VimeoPlayer(this.iframe);
    return player;
  }

  getSrc() {
    const _mainAsset = this.props.item.assets.main;
    const _asset = assetUtils.assetFromRef(_mainAsset);
    const _assetPath = _asset.path;
    const _id = this.extractId(_assetPath);

    const src = 'https://player.vimeo.com/video/' + _id + '?autopause=0&loop=1';

    return src;
  }

  extractId(path) {
    let id;
    const regExp = /^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/)|(video\/))?([0-9]+)/;
    const match = path.match(regExp);

    if (match) {
      id = match[6];
    }

    return id;
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Common.Model.Entity.DisplayItem} item DisplayItem object.
 * @property {Object} itemOpts Object defining the various item options.
 */
Vimeo.propTypes = {
  item:     PropTypes.oneOfType([PropTypes.instanceOf(DisplayItem), PropTypes.instanceOf(PinnedDisplayItem)]),
  itemOpts: PropTypes.object,
  styles:   PropTypes.shape({
    width:  PropTypes.number,
    height: PropTypes.number,
  }),
};

/**
 * React default props.
 *
 * @type {Object}
 */
Vimeo.defaultProps = {
  styles: {},
};

export { Vimeo };

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * <pre>
 * Instagram.
 *
 * Summary of responsibilities:
 *
 * 	- extracts the id from the DisplayItem as appropriate to build the video url.
 *  - generates markup to output an embedded Instagram video.
 * </pre>
 *
 * @namespace
 * @memberof Common.Components.VideoPlayers
 */
class Instagram extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
  }

  componentDidMount() {
    const instagramScript = document.createElement('script');

    instagramScript.async = true;
    instagramScript.defer = true;
    instagramScript.src = 'https://platform.instagram.com/en_US/embeds.js';
    instagramScript.id = 'instagram-embed-script';

    const body = document.body;
    if (body && !document.getElementById('instagram-embed-script')) {
      body.appendChild(instagramScript);
    } else {
      instgrm.Embeds.process();
    }
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {ReactElement}
   */
  render() {
    if (
      !(this.props.item.meta.network === 'instagram' || this.props.item.meta.network === 'facebook') &&
      !(this.props.item.meta.network === 'ugc' && this.props.item.content.link)
    ) {
      return false;
    }
    // Otherwise, render the video
    const link = _.get(this.props, 'item.content.link');
    const thumbNail = assetUtils.assetFromRef(_.get(this.props, 'item.assets.thumbnail'));
    return (
      <div className="xEmbed xEmbedInstagram xEmbedVideoPlayer">
        <blockquote className="instagram-media" data-instgrm-permalink={link} data-instgrm-version="12">
          <div className="xAssetImage">
            <img className="xThumbnail" src={thumbNail.src} alt={'Content Image'} />
          </div>
        </blockquote>
        {/* For posts with short descriptions, this will maintain a reasonable width */}
        <div className="xAssetImage xNoHeight">
          <img className="xThumbnail xNoHeight" src={thumbNail.src} alt={'Content Image'} />
        </div>
      </div>
    );
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Common.Model.Entity.DisplayItem} item DisplayItem object.
 * @property {Object} itemOpts Object defining the various item options.
 */
Instagram.propTypes = {
  item:     PropTypes.instanceOf(DisplayItem),
  itemOpts: PropTypes.object,
};

export { Instagram };

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * <pre>
 * Vine.
 *
 * Summary of responsibilities:
 *
 * 	- extracts the id from the DisplayItem as appropriate to build the video url.
 *  - generates markup to output an embedded Vine video.
 * </pre>
 *
 * @namespace
 * @memberof Common.Components.VideoPlayers
 */
class Vine extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Lifecycle methods */

  /**
   * Called when component mounts for the first time.
   * Loads the Vine SDK.
   */
  componentDidMount() {
    // Load lib.
    const script = document.createElement('script');

    script.src = '//platform.vine.co/static/scripts/embed.js';
    script.async = true;
    script.charset = 'utf-8';

    document.body.appendChild(script);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {ReactElement}
   */
  render() {
    const src = this.getSrc();

    return (
      <div className="xEmbed xEmbedVine xEmbedVideoPlayer">
        <iframe className="vine-embed" src={src} width="600" height="600" frameBorder="0" />
      </div>
    );
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Utility methods */

  getSrc() {
    const _mainAsset = this.props.item.assets.main;
    const _asset = assetUtils.assetFromRef(_mainAsset);
    const _assetPath = _asset.path;
    const _id = this.extractId(_assetPath);

    const src = 'https://vine.co/v/' + _id + '/embed/simple';

    return src;
  }

  extractId(path) {
    let _path = path;

    // Strip query string first
    if (path.indexOf('?') != -1) {
      _path = path.substring(0, path.indexOf('?'));
    }

    const startIndex = _path.lastIndexOf('/v/') + 3;
    let id = _path.substring(startIndex);

    if (id.indexOf('/') > -1) {
      id = id.substring(0, id.indexOf('/'));
    }

    return id;
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Common.Model.Entity.DisplayItem} item DisplayItem object.
 * @property {Object} itemOpts Object defining the various item options.
 */
Vine.propTypes = {
  item:     PropTypes.instanceOf(DisplayItem),
  itemOpts: PropTypes.object,
};

export { Vine };

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * <pre>
 * Youtube.
 *
 * Summary of responsibilities:
 *
 * 	- extracts the id from the DisplayItem as appropriate to build the video url.
 *  - generates markup to output an embedded Youtube video.
 * </pre>
 *
 * @namespace
 * @memberof Common.Components.VideoPlayers
 */
class Youtube extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {ReactElement}
   */
  render() {
    const { width } = this.props.styles;
    return (
      <div className="xEmbed xEmbedYoutube xEmbedVideoPlayer" style={{ width: width ? `${width}px` : undefined }}>
        {this.getPlayer(this.props.shouldAutoPlay, this.props.isMuted, this.props.parentWidth)}
      </div>
    );
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Utility methods */

  getPlayer(shouldAutoPlay, isMuted, parentWidth) {
    const _id = this.getId();
    const options = {
      playerVars: {
        playlist: _id, // Required for looping. https://developers.google.com/youtube/player_parameters#loop
        loop:     1,
        autoplay: shouldAutoPlay ? 1 : 0,
      },
    };
    return (
      <YoutubePlayer
        videoId={_id}
        onReady={(e) => {
          if (isMuted) {
            e.target.mute();
          } else {
            e.target.unMute();
            e.target.setVolume(50);
          }
        }}
        opts={options}
      />
      // <iframe
      //   id="player"
      //   type="text/html"
      //   width="100%"
      //   src={`https://www.youtube.com/embed/${this.getId()}`}
      //   frameBorder="0"
      //   autoPlay={shouldAutoPlay ? 1 : 0}
      //   loop={1}
      // />
    );
  }

  getId() {
    const _mainAsset = this.props.item.assets.main;
    const _asset = assetUtils.assetFromRef(_mainAsset);
    const _assetPath = _asset.path;
    const _id = this.extractId(_assetPath);
    return _id;
  }

  getSrc() {
    const _id = this.getId();
    let src = '//www.youtube.com/embed/' + _id;

    if (this.props.itemOpts.customProperties) {
      src += '?' + this.props.itemOpts.customProperties;
    }

    return src;
  }

  extractId(path) {
    var patt = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/i;
    try {
      return patt.exec(path)[1];
    } catch (e) {
      return '';
    }
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Common.Model.Entity.DisplayItem} item DisplayItem object.
 * @property {Object} itemOpts Object defining the various item options.
 */
Youtube.propTypes = {
  item:   PropTypes.oneOfType([PropTypes.instanceOf(DisplayItem), PropTypes.instanceOf(PinnedDisplayItem)]),
  styles: PropTypes.shape({
    width:  PropTypes.number,
    height: PropTypes.number,
  }),
  itemOpts: PropTypes.object,
};

/**
 * React default props.
 *
 * @type {Object}
 */
Youtube.defaultProps = {
  styles: {},
};

export { Youtube };

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * <pre>
 * Twitter.
 *
 * Summary of responsibilities:
 *
 * 	- extracts the id from the DisplayItem as appropriate to build the video url.
 *  - generates markup to output an embedded Twitter video.
 * </pre>
 *
 * @namespace
 * @memberof Common.Components.VideoPlayers
 */
class Twitter extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
  }

  componentDidMount() {
    // autoplays video even when not muted by default (overlays)
    const video = this.video;
    !!video && this.props.shouldAutoPlay && video.addEventListener('canplay', video.play);
  }

  componentWillUnmount() {
    const video = this.video;
    !!video && this.props.shouldAutoPlay && video.removeEventListener('canplay', video.play);
  }

  handleRef(refName) {
    this[`set${refName}`] = (domEle) => (this[refName] = domEle);
    return this[`set${refName}`];
  }
  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {ReactElement}
   */
  render() {
    const mainAsset = this.props.item.assets.main;
    const src = assetUtils.assetFromRef(mainAsset).path;
    return (
      <div className="xEmbed xEmbedTwitterVideo xEmbedVideoPlayer">
        <video
          ref={this.handleRef('video')}
          muted={this.props.isMuted}
          src={src}
          autoPlay={this.props.shouldAutoPlay}
          playsInline
          loop
          controls
        />
      </div>
    );
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Common.Model.Entity.DisplayItem} item DisplayItem object.
 * @property {Object} itemOpts Object defining the various item options.
 */
Twitter.propTypes = {
  item:     PropTypes.instanceOf(DisplayItem),
  itemOpts: PropTypes.object,
};

export { Twitter };

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * <pre>
 * Facebook.
 *
 * Summary of responsibilities:
 *
 * 	- extracts the id from the DisplayItem as appropriate to build the video url.
 *  - generates markup to output an embedded Facebook video.
 * </pre>
 *
 * @namespace
 * @memberof Common.Components.VideoPlayers
 */
class Facebook extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
    //This resets when the component is re-created
    this.state = { iFrameActivated: false };
  }

  shouldComponentUpdate(nextProps) {
    //prevent un-needed re-renders
    //React.PureComponent does a shallow check, i still do not understand why it does not do a deep
    //check for state changes
    return !_.isEqual(this.props, nextProps);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Lifecycle methods */

  /**
   * Called when component mounts for the first time.
   * Loads the Twitter SDK.
   */
  componentDidMount() {
    const me = this;
    requireSDK('fb', () => {
      FB.Event.subscribe('xfbml.ready', function (msg) {
        if (msg.type === 'video' && me.state.iFrameActivated === false) {
          me.setState({ iFrameActivated: true });
          NGX.trigger('overlay:content:rendered');
        }
      });

      FB.XFBML.parse();
    });
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {ReactElement}
   */
  render() {
    const assetPage = this.props.item.content.link;

    return (
      <div
        className="fb-video"
        data-href={assetPage}
        data-allowfullscreen="true"
        data-width="auto"
        data-autoplay="true"
      />
    );
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Common.Model.Entity.DisplayItem} item DisplayItem object.
 * @property {Object} itemOpts Object defining the various item options.
 */
Facebook.propTypes = {
  item:     PropTypes.instanceOf(DisplayItem),
  itemOpts: PropTypes.object,
};

export { Facebook };

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * <pre>
 * Generic Video.
 * </pre>
 *
 * @namespace
 * @memberof Common.Components.VideoPlayers
 */
class GenericVideo extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);
    this.setVideoRef = this.setVideoRef.bind(this);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Lifecycle methods */

  componentDidMount() {
    const video = this.video;
    !!video && this.props.shouldAutoPlay && video.addEventListener('canplay', video.play); // Needed to autoplay in Chrome - Eric 6-5-18
  }

  componentWillUnmount() {
    const video = this.video;
    !!video && this.props.shouldAutoPlay && video.removeEventListener('canplay', video.play);
  }

  setVideoRef(domEle) {
    this.video = domEle;
  }
  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {ReactElement}
   */
  render() {
    const mainAsset = this.props.item.assets.main;
    const src = assetUtils.assetFromRef(mainAsset).path;

    return (
      <div className="xEmbed xEmbedVideoPlayer xEmbedGenericVideo">
        <video
          ref={this.setVideoRef}
          src={src}
          autoPlay={this.props.shouldAutoPlay}
          playsInline
          loop
          controls
          muted={this.props.isMuted}
        />
      </div>
    );
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Common.Model.Entity.DisplayItem} item DisplayItem object.
 * @property {Object} itemOpts Object defining the various item options.
 */
GenericVideo.propTypes = {
  item:     PropTypes.instanceOf(DisplayItem),
  itemOpts: PropTypes.object,
};

export { GenericVideo };

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * <pre>
 * Video stream (Mux).
 * </pre>
 *
 * @namespace
 * @memberof Common.Components.VideoPlayers
 */
class Stream extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);

    // Set initial properties.
    this.hls = null;
    this.player = null;
    this.playerControls = null;
    this.playerControlsOverlay = null;
    this.hideControlsClass = 'xHideControls';
    this.hideControlsTimeout = 2000;
    this.currentPlaylistIndex = 0;
    this.mainItemIndex = null;

    // Generate playlist.
    this.playlist = this.generatePlaylist(props);

    // Bind methods.
    this.setVideoRef = this.setVideoRef.bind(this);
    this.initStream = this.initStream.bind(this);
    this.destroyStream = this.destroyStream.bind(this);
    this.getCurrentPlaylistItem = this.getCurrentPlaylistItem.bind(this);
    this.getPlaylistItem = this.getPlaylistItem.bind(this);
    this.advancePlaylist = this.advancePlaylist.bind(this);
    this.resetPlaylist = this.resetPlaylist.bind(this);
    this.hidePlayerControls = this.hidePlayerControls.bind(this);
    this.showPlayerControls = this.showPlayerControls.bind(this);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Lifecycle methods */
  componentDidMount() {
    // If the main playlist item is ready.
    if (this.getPlaylistItem(this.mainItemIndex).status === enums.MUX_STATUS_READY) {
      // Define Plyr controls.
      const controls = ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume'];

      // Don't add fullscreen option for iOS Safari or other unsupported browsers.
      const htmlClassList = document.documentElement.classList;
      const isIOS = htmlClassList.contains('uaIphone');
      const isSafari = htmlClassList.contains('uaSafari');
      if (Hls.isSupported() && !(isIOS && isSafari)) {
        controls.push('fullscreen');
      }

      // Create instance of Plyr.
      this.player = new Plyr(this.video, {
        controls: controls,
      });

      // Store ref to player controls so that we can forcibly hide them if required.
      this.playerControls = document.querySelector('.plyr__controls');
      this.playerControlsOverlay = document.querySelector('.plyr__control--overlaid');

      // Listen for video events.
      this.video.addEventListener(
        'ended',
        () => {
          // Advance the playlist.
          this.advancePlaylist();

          // Get next playlist item.
          const nextPlaylistItem = this.getCurrentPlaylistItem();

          // If we have NOT reached the end of the playlist.
          if (nextPlaylistItem) {
            this.initStream();

            // If we HAVE reached the end of the playlist.
          } else {
            this.resetPlaylist();
            this.initStream(false);
          }
        },
        false
      );

      // Init the stream.
      this.initStream();
    }
  }

  componentWillUnmount() {
    // Destroy the stream.
    this.destroyStream();
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Playlist methods */
  generatePlaylist(props) {
    // Array of assets to play in sequence.
    const playlist = [];

    // Do we have a pre-roll asset ?
    if (props.itemOpts.preRollVideoAssetRef) {
      playlist.push(this.parsePlaylistItem(props.itemOpts.preRollVideoAssetRef, 'pre'));
    }

    // Do we have a main asset ?
    if (props.item.assets.main) {
      playlist.push(this.parsePlaylistItem(props.item.assets.main, 'main'));
      this.mainItemIndex = playlist.length - 1;
    }

    // Do we have a post-roll asset ?
    if (props.itemOpts.postRollVideoAssetRef) {
      playlist.push(this.parsePlaylistItem(props.itemOpts.postRollVideoAssetRef, 'post'));
    }

    return playlist;
  }

  parsePlaylistItem(assetRef, type) {
    // Parse the asset.
    const asset = assetUtils.assetFromRef(assetRef);

    // The asset status.
    const status = asset.meta.status;

    // Get the Mux id.
    const id = asset.meta.assetId;

    // Get the Mux playback id.
    const pathParts = asset.path.split(':');
    const playbackId = pathParts[0];

    // Define the Mux playback url.
    const playbackUrl = 'https://stream.mux.com/' + playbackId + '.m3u8';

    return {
      id:          id,
      type:        type,
      status:      status,
      playbackId:  playbackId,
      playbackUrl: playbackUrl,
    };
  }

  getCurrentPlaylistItem() {
    return this.getPlaylistItem(this.currentPlaylistIndex);
  }

  getPlaylistItem(index) {
    return this.playlist[index];
  }

  advancePlaylist() {
    this.currentPlaylistIndex++;
  }

  resetPlaylist() {
    this.currentPlaylistIndex = 0;
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Stream methods */
  initStream(autoplay = true) {
    // Get the current playlist item.
    const currentPlaylistItem = this.getCurrentPlaylistItem();

    // If browser supports Hls.js
    if (Hls.isSupported()) {
      // If we are autoplaying/transitioning between playlist items, avoid a glitch whereby
      // Plyr unavoidably briefly flashes the control bar/overlay icon up on screen.
      if (autoplay) {
        this.hidePlayerControls();
      }

      // Create new Hls.js instance.
      this.hls = new Hls();

      // Bind Hls.js and native video element together.
      this.hls.attachMedia(this.video);

      // Listen for media attached event.
      this.hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        // Load the stream url.
        this.hls.loadSource(currentPlaylistItem.playbackUrl);

        // Listen for the parsed event.
        this.hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
          // Autoplay the video.
          if (autoplay) {
            this.player.play();

            // Re-enable the controls after the specified timeout.
            setTimeout(() => {
              this.showPlayerControls();
            }, this.hideControlsTimeout);
          }
        });
      });

      // Otherwise fallback to native Video element (iOS Safari).
    } else {
      // Set the native video element source.
      this.video.src = currentPlaylistItem.playbackUrl;

      // This forces videos to autoplay on IOS Safari.
      this.video.addEventListener('loadedmetadata', () => {
        if (autoplay) {
          this.video.play();
        }
      });
    }
  }

  destroyStream() {
    // Destroy Plyr instance.
    if (this.player) {
      this.player.destroy();
    }

    // Destroy Hls.js instance.
    if (this.hls) {
      this.hls.destroy();
    }

    // Reset native video source.
    if (this.video) {
      this.video.src = null;
    }
  }

  hidePlayerControls() {
    this.playerControls.classList.add(this.hideControlsClass);
    this.playerControlsOverlay.classList.add(this.hideControlsClass);
  }

  showPlayerControls() {
    this.playerControls.classList.remove(this.hideControlsClass);
    this.playerControlsOverlay.classList.remove(this.hideControlsClass);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {ReactElement}
   */
  render() {
    let videoEl;
    if (this.getPlaylistItem(this.mainItemIndex).status === enums.MUX_STATUS_READY) {
      videoEl = <video id={this.getPlaylistItem(this.mainItemIndex).playbackId} ref={this.setVideoRef} />;
    }

    return (
      <div className="xEmbed xEmbedVideoPlayer xEmbedStream">
        <div className="xStreamWrapper">{videoEl}</div>
      </div>
    );
  }

  setVideoRef(domEle) {
    this.video = domEle;
  }
}

export { Stream };
